import { IField } from '../data-models/field2.data-model';
import { createFormFromFieldsArray } from '../view-models/form.view-model';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { capitalize } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormReturn, useFormContext } from 'react-hook-form';
import * as yup from 'yup';
import { KpiConfigPeriod, KpiConfigPeriodToName } from '../data-models/company-financials.data-model';
import { RendererType } from '../data-models/field-definition';
import {
  FieldEntity,
  ISimpleChoice,
  KpiConfigScenario,
  KpiConfigScenarioToName,
  KpiConfigSection,
} from '../data-models/field2.data-model';
import { ISelectMeta } from '../data-models/field3.data-model';
import { getKpiFormFields, lockUniversalKpiFields } from '../pages/Config/KPI/KPIFields';
import { kpiGroupsAtom } from '../services/state/KPIConfigState';
import { MaggieFeatureFlags } from '../util/feature-flags';
import { schemaToFormFields } from '../util/schema-utils';
import { IForm } from '../view-models/form.view-model';

const kpiFormatOptions = [
  { value: RendererType.currency, displayName: 'currency', secondaryLabel: '$123' },
  { value: RendererType.date, displayName: 'date', secondaryLabel: 'yyyy-MM-dd' },
  { value: RendererType.integer, displayName: 'integer', secondaryLabel: '123' },
  { value: RendererType.numeric, displayName: 'number', secondaryLabel: '123.45' },
  { value: RendererType.multiplier, displayName: 'multiplier', secondaryLabel: '123X' },
  { value: RendererType.percent, displayName: 'percent', secondaryLabel: '123%' },
  { value: RendererType.text, displayName: 'text', secondaryLabel: 'Text' },
] as const;

const kpiEntityOptions = [
  { value: FieldEntity.universalKPI, displayName: 'Universal KPI' },
  { value: FieldEntity.userKPI, displayName: 'Custom KPI' },
] as const;

export function useGetKPIGroupField() {
  const kpiGroups = useAtomValue(kpiGroupsAtom);

  return useCallback(
    (fieldId: string, label: string) => {
      const groups: ISimpleChoice<number>[] = kpiGroups?.map((group) => {
        return {
          value: group.id,
          displayName: group.name,
        };
      });
      return yup
        .number()
        .nullable()
        .default(null)
        .transform((value, originalValue) => (value === '' || originalValue === '' ? null : value))
        .label(label)
        .formMeta({
          renderer: RendererType.singleSelect,
          rendererMeta: {
            values: groups,
          } as ISelectMeta<number>,
        });
    },
    [kpiGroups]
  );
}

/**
 * Default values are set for textual KPIs
 */
export function useKpiConfigFields() {
  const getKPIGroupField = useGetKPIGroupField();
  const { showFileKpiConfig } = useFlags<MaggieFeatureFlags>();

  return useMemo(() => {
    const formatOptions = [
      ...kpiFormatOptions,
      ...(showFileKpiConfig
        ? [{ value: RendererType.fileUpload, displayName: 'file upload', secondaryLabel: 'File' } as const]
        : []),
    ];

    return {
      displayName: yup.string().required('Name is required').label('Name').formMeta({
        renderer: RendererType.text,
      }),

      description: yup.string().nullable().default(null).label('Description').formMeta({
        renderer: RendererType.text,
      }),

      entity: yup
        .string()
        .nullable()
        .oneOf([FieldEntity.universalKPI, FieldEntity.userKPI], 'Please select a valid KPI category')
        .default(FieldEntity.userKPI)
        .required('Category is required')
        .label('Category')
        .formMeta({
          renderer: RendererType.singleSelect,
          rendererMeta: {
            values: [...kpiEntityOptions],
            formatValueToLabel: true,
          } as ISelectMeta<string>,
          disableClearable: true,
        }),

      formMeta: yup.object({
        renderer: yup.object({
          type: yup
            .string()
            .oneOf(
              formatOptions.map((opt) => opt.value),
              'Please select a valid type'
            )
            .required('Type is required')
            .default(RendererType.text)
            .label('Type')
            .formMeta({
              renderer: RendererType.singleSelect,
              rendererMeta: {
                values: [...formatOptions],
                formatValueToLabel: true,
              } as ISelectMeta<string>,
              disableClearable: true,
            }),
        }),
        // Placeholder holding the Format field's slot under Type; syncFormatterField fills in the
        // options the chosen Type allows, or drops it for a Type with no configurable family.
        formatter: yup.object({
          id: yup.string().nullable().optional().label('Format').formMeta({
            renderer: RendererType.singleSelect,
          }),
        }),
      }),
      id: yup.number().nullable().default(null),
      kpiGroupId: getKPIGroupField('kpiGroupId', 'Group'),
      gridMeta: yup.object({
        aggFunc: yup
          .string()
          .nullable()
          .default(null)
          .label('Column Aggregation')
          .formMeta({
            description: 'Aggregation for the KPI column in the Portfolio Reporting grid',
            renderer: RendererType.singleSelect,
            rendererMeta: {
              values: ['avg', 'sum'].map((value) => ({
                value,
                displayName: value.toUpperCase(),
              })),
              formatValueToLabel: true,
            } as ISelectMeta<string>,
          }),
      }),

      sectionType: yup
        .string()
        .oneOf(Object.values(KpiConfigSection), 'Please select a valid scenario')
        .default(KpiConfigSection.Both)
        .label('Scenario')
        .formMeta({
          renderer: RendererType.singleSelect,
          rendererMeta: {
            values: Object.values(KpiConfigSection).map((value) => ({
              value,
              displayName: capitalize(value),
            })),
            formatValueToLabel: true,
          } as ISelectMeta<string>,
        }),

      kpiScenario: yup
        .string()
        .oneOf(Object.values(KpiConfigScenario))
        .default(KpiConfigScenario.EndOfPeriod)
        .label('Aggregation')
        .formMeta({
          description:
            "Accrued KPIs are aggregated for quarterly and yearly totals, while End of Period KPIs use the most recent month's value for these totals",
          renderer: RendererType.singleSelect,
          rendererMeta: {
            values: Object.entries(KpiConfigScenarioToName).map(([key, value]) => ({
              value: key,
              displayName: value,
            })),
            formatValueToLabel: true,
          } as ISelectMeta<string>,
        }),

      periods: yup
        .array()
        .of(yup.string().oneOf(Object.values(KpiConfigPeriod)))
        .default([KpiConfigPeriod.Latest])
        .label('Period')
        .formMeta({
          description:
            'Please choose the periods that you want to display for this KPI in the portfolio reporting grid',
          renderer: RendererType.multiSelect,
          rendererMeta: {
            values: Object.entries(KpiConfigPeriodToName).map(([key, value]) => ({
              value: key,
              displayName: value,
            })),
            formatValueToLabel: true,
          } as ISelectMeta<string>,
        }),
    };
  }, [getKPIGroupField, showFileKpiConfig]);
}

/**
 * Full KPI config schema for form validation and type inference.
 */
export function useKpiConfigSchema() {
  const kpiConfigFields = useKpiConfigFields();
  return useMemo(() => yup.object(kpiConfigFields), [kpiConfigFields]);
}

export function useDynamicKpiConfigForm() {
  const methods = useFormContext<IField<unknown>>();
  const flags = useFlags<MaggieFeatureFlags>();
  const kpiSchema = useKpiConfigSchema();

  const initialForm = useMemo(
    () =>
      createFormFromFieldsArray<IField<unknown>>(
        schemaToFormFields(kpiSchema, [
          'displayName',
          'description',
          'entity',
          'formMeta.renderer.type',
          'formMeta.formatter.id',
          'kpiGroupId',
          'kpiScenario',
          'periods',
          ...(flags.showAggFuncInKpiConfig ? (['gridMeta.aggFunc'] as const) : []),
        ] as const)
      ),
    [flags.showAggFuncInKpiConfig, kpiSchema]
  );

  const [form, setForm] = useState(initialForm);
  useEffect(() => {
    // getKpiFormFields provides format-dependent sync logic (periods ↔ scenario, aggFunc visibility).
    // This form needs stricter disabling than the legacy admin form, hence applySchemaFormEntityRestrictions.
    const dynamicForm = getKpiFormFields(initialForm, methods, flags);
    applySchemaFormEntityRestrictions(dynamicForm, methods);
    setForm(dynamicForm);
  }, [initialForm, methods, flags]);

  return form;
}

/**
 * Edit-mode restrictions for the schema-based KPI config form.
 * - All KPIs: format and entity are locked (changing these on an existing KPI is not allowed)
 * - Universal KPIs: all fields locked except displayName, description, and kpiGroupId
 *   (shared with the admin form via lockUniversalKpiFields — same backend PUT contract).
 *
 * This is specific to the schema-based form — the admin form
 * (ConfigKPI.tsx → getAdminKpiFormFields) has its own restriction logic; only the
 * universal-KPI lock (above) is shared.
 */
export function applySchemaFormEntityRestrictions(
  form: IForm<IField<unknown>>,
  methods: UseFormReturn<IField<unknown>>
) {
  const id = methods.watch('id');
  if (id == null) {
    return;
  }

  for (const key of ['formMeta.renderer.type', 'entity']) {
    const field = form.fields.get(key);
    if (field) {
      form.fields.set(key, { ...field, disabled: true });
    }
  }

  const entity = methods.watch('entity');
  if (entity === FieldEntity.universalKPI) {
    lockUniversalKpiFields(form);
  }
}
